import { type FormEvent, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, ConfirmModal, Loader, useToast } from '@lms/ui';
import { SearchEmptyState } from '@/components/SearchEmptyState';
import { SearchInput } from '@/components/SearchInput';
import { HubBackLink } from '@/components/hub/HubBackLink';
import { api, listResource } from '@/lib/api';
import { matchesSearch } from '@/lib/search-normalize';
import { credentialTemplateSuccessToast, roleActionErrorToast } from '@/lib/user-feedback';

interface CredentialTemplate {
  id: string;
  name: string;
  title?: string;
  typeCode?: string | null;
  typeName?: string | null;
  versionNumber?: number | null;
  orientation?: string | null;
  credentialsCount?: number;
}

const CREDENTIAL_TYPES = [
  { id: 'certificate', name: 'Certificado' },
  { id: 'diploma', name: 'Diploma' },
  { id: 'badge', name: 'Insignia' },
];

const ORIENTATIONS = [
  { value: 'landscape', label: 'Horizontal' },
  { value: 'portrait', label: 'Vertical' },
];

const PER_PAGE = 20;

const MODAL_FIELD =
  'w-full rounded-xl border border-dash-outline-variant bg-white py-3 px-4 text-base outline-none transition-all focus:border-dash-secondary focus:ring-2 focus:ring-dash-secondary/20';

function orientationLabel(orientation?: string | null): string {
  switch (orientation) {
    case 'portrait':
      return 'Vertical';
    case 'landscape':
      return 'Horizontal';
    default:
      return '—';
  }
}

export function CredentialTemplatesPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [page, setPage] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<CredentialTemplate | null>(null);

  const [newName, setNewName] = useState('');
  const [newType, setNewType] = useState('');
  const [newOrientation, setNewOrientation] = useState('landscape');
  const [newDescription, setNewDescription] = useState('');

  const { data, isLoading, error } = useQuery({
    queryKey: ['credentials', 'templates'],
    queryFn: () => listResource<CredentialTemplate>('/credentials/templates'),
  });

  const createMutation = useMutation({
    mutationFn: (input: { name: string; orientation: string }) =>
      api<{ id: string }>('/credentials/templates', { method: 'POST', body: input }),
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ['credentials', 'templates'] });
      setModalOpen(false);
      resetModalForm();
      toast(credentialTemplateSuccessToast('create'));
      navigate(`/credenciales/plantillas/${created.id}/editor`);
    },
    onError: (error) => {
      toast(roleActionErrorToast(error, 'No se pudo crear la plantilla de credencial'));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api(`/credentials/templates/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['credentials', 'templates'] });
      setDeleteTarget(null);
      toast(credentialTemplateSuccessToast('delete'));
    },
    onError: (error) => {
      toast(roleActionErrorToast(error, 'No se pudo eliminar la plantilla de credencial'));
    },
  });

  const filtered = useMemo(() => {
    let rows = data?.data ?? [];
    if (search.trim()) {
      rows = rows.filter((template) => matchesSearch(template.name ?? template.title, search));
    }
    if (typeFilter) {
      rows = rows.filter((template) => template.typeCode === typeFilter);
    }
    return rows;
  }, [data?.data, search, typeFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const currentPage = Math.min(page, totalPages);
  const pageRows = filtered.slice((currentPage - 1) * PER_PAGE, currentPage * PER_PAGE);
  const firstItem = filtered.length === 0 ? 0 : (currentPage - 1) * PER_PAGE + 1;
  const lastItem = Math.min(currentPage * PER_PAGE, filtered.length);

  function resetModalForm() {
    setNewName('');
    setNewType('');
    setNewOrientation('landscape');
    setNewDescription('');
  }

  useEffect(() => {
    setPage(1);
  }, [search, typeFilter]);

  function handleCreateSubmit(e: FormEvent) {
    e.preventDefault();
    if (!newName.trim()) return;
    createMutation.mutate({ name: newName.trim(), orientation: newOrientation });
  }

  return (
    <div className="lms-credential-templates lms-site-admin-hub-page font-sans">
      <div className="mb-6 flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
        <div className="flex items-start gap-4">
          <HubBackLink to="/administracion?tab=general" label="Volver a Administración" />
          <div>
            <h1 className="text-3xl font-semibold text-dash-primary md:text-4xl">Plantillas</h1>
            <p className="mt-1 max-w-2xl text-dash-on-surface-variant">
              Gestor de plantillas para certificados, diplomas y badges
            </p>
          </div>
        </div>
        <button
          type="button"
          className="inline-flex items-center justify-center gap-2 rounded-xl bg-dash-primary px-6 py-3 text-sm font-medium text-white shadow-sm transition-all hover:opacity-90 active:scale-[0.98]"
          onClick={() => setModalOpen(true)}
        >
          <span className="material-symbols-outlined text-[1.25rem]" aria-hidden="true">
            add
          </span>
          Nueva plantilla
        </button>
      </div>

      <form
        method="get"
        action="/credenciales/plantillas"
        className="lms-credentials-glass-panel mb-8 space-y-6 rounded-2xl p-6 shadow-sm"
        onSubmit={(event) => event.preventDefault()}
      >
        <div className="grid grid-cols-1 items-end gap-4 md:grid-cols-12">
          <div className="space-y-2 md:col-span-8">
            <label htmlFor="template-search" className="ml-1 text-sm font-medium text-dash-on-surface-variant">
              Buscar por nombre
            </label>
            <SearchInput
              id="template-search"
              name="search"
              value={search}
              onChange={setSearch}
              placeholder="Ej: Certificado de Excelencia..."
              aria-label="Buscar por nombre"
            />
          </div>
          <div className="space-y-2 md:col-span-4">
            <label htmlFor="credential_type_id" className="ml-1 text-sm font-medium text-dash-on-surface-variant">
              Tipo de credencial
            </label>
            <div className="relative">
              <select
                id="credential_type_id"
                name="credential_type_id"
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="w-full cursor-pointer appearance-none rounded-xl border border-dash-outline-variant bg-white py-3 pl-4 pr-10 text-base outline-none transition-all focus:border-dash-secondary focus:ring-2 focus:ring-dash-secondary/20"
              >
                <option value="">Todos</option>
                {CREDENTIAL_TYPES.map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.name}
                  </option>
                ))}
              </select>
              <span
                className="material-symbols-outlined pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-secondary-400"
                aria-hidden="true"
              >
                keyboard_arrow_down
              </span>
            </div>
          </div>
        </div>
      </form>

      {error && (
        <p className="mb-4 text-sm text-red-600" role="alert">
          {(error as Error).message}
        </p>
      )}

      <section className="lms-gradebook lms-gradebook--forum">
        <div className="lms-course-forum-card">
          {isLoading ? (
            <div className="flex justify-center py-12">
              <Loader />
            </div>
          ) : filtered.length === 0 ? (
            <div className="lms-gradebook__table-wrap">
              <div className="lms-gradebook__empty">
                <div className="lms-gradebook__empty-icon" aria-hidden="true">
                  <span className="material-symbols-outlined" style={{ fontVariationSettings: "'FILL' 1" }}>
                    workspace_premium
                  </span>
                </div>
                {search.trim() || typeFilter ? (
                  <SearchEmptyState
                    term={search.trim()}
                    onClear={() => {
                      setSearch('');
                      setTypeFilter('');
                    }}
                  />
                ) : (
                  <>
                    <h3>Sin plantillas</h3>
                    <p>Crea una plantilla para comenzar a emitir credenciales.</p>
                  </>
                )}
              </div>
            </div>
          ) : (
            <>
              <div className="lms-gradebook__table-wrap">
                <table className="lms-gradebook__table">
                  <thead>
                    <tr>
                      <th className="lms-gradebook__th" scope="col">
                        Nombre
                      </th>
                      <th className="lms-gradebook__th" scope="col">
                        Tipo
                      </th>
                      <th className="lms-gradebook__th" scope="col">
                        Versión
                      </th>
                      <th className="lms-gradebook__th" scope="col">
                        Orientación
                      </th>
                      <th className="lms-gradebook__th lms-gradebook__th--actions" scope="col">
                        Acciones
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageRows.map((template) => {
                      const label = template.name ?? template.title ?? 'Plantilla';
                      const typeCode = template.typeCode;
                      const canDelete = (template.credentialsCount ?? 0) === 0;
                      const initials = label
                        .trim()
                        .split(/\s+/)
                        .filter(Boolean)
                        .slice(0, 2)
                        .map((part) => part.charAt(0).toUpperCase())
                        .join('');

                      return (
                        <tr key={template.id}>
                          <th className="lms-gradebook__td" scope="row">
                            <div className="lms-gradebook__student">
                              <div className="lms-gradebook__avatar" aria-hidden="true">
                                {initials || '?'}
                              </div>
                              <Link to={`/credenciales/plantillas/${template.id}`} className="lms-gradebook__name">
                                {label}
                              </Link>
                            </div>
                          </th>
                          <td className="lms-gradebook__td">
                            <Badge
                              variant={
                                typeCode === 'badge'
                                  ? 'primary'
                                  : typeCode === 'certificate' || typeCode === 'diploma'
                                    ? 'secondary'
                                    : 'default'
                              }
                            >
                              {template.typeName ?? '—'}
                            </Badge>
                          </td>
                          <td className="lms-gradebook__td">
                            <span className="lms-gradebook__muted">
                              {template.versionNumber != null ? `v${template.versionNumber}` : '—'}
                            </span>
                          </td>
                          <td className="lms-gradebook__td">
                            <span className="lms-gradebook__muted">{orientationLabel(template.orientation)}</span>
                          </td>
                          <td className="lms-gradebook__td lms-gradebook__td--actions">
                            <div className="flex items-center justify-end gap-1">
                              <Link
                                to={`/credenciales/plantillas/${template.id}`}
                                className="lms-gradebook__action"
                                title="Ver"
                                aria-label={`Ver ${label}`}
                              >
                                <span className="material-symbols-outlined" aria-hidden="true">
                                  visibility
                                </span>
                              </Link>
                              <Link
                                to={`/credenciales/plantillas/${template.id}/editor`}
                                className="lms-gradebook__action"
                                title="Editar"
                                aria-label={`Editar ${label}`}
                              >
                                <span className="material-symbols-outlined" aria-hidden="true">
                                  edit
                                </span>
                              </Link>
                              <button
                                type="button"
                                className="lms-gradebook__action"
                                title={
                                  canDelete
                                    ? 'Eliminar'
                                    : 'No se puede eliminar: hay credenciales emitidas con esta plantilla'
                                }
                                aria-label={`Eliminar ${label}`}
                                disabled={!canDelete}
                                onClick={() => setDeleteTarget(template)}
                              >
                                <span className="material-symbols-outlined" aria-hidden="true">
                                  delete
                                </span>
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="lms-course-forum-card__footer">
                <span className="lms-course-forum-card__meta">
                  Mostrando <strong>{firstItem}</strong> a <strong>{lastItem}</strong> de{' '}
                  <strong>{filtered.length}</strong> resultados
                </span>
                <div className="lms-course-forum-card__pager">
                  <button
                    type="button"
                    className="lms-course-forum-card__pager-btn"
                    aria-label="Página anterior"
                    disabled={currentPage <= 1}
                    onClick={() => setPage((current) => Math.max(1, current - 1))}
                  >
                    <span className="material-symbols-outlined" aria-hidden="true">
                      chevron_left
                    </span>
                  </button>
                  <span className="lms-course-forum-card__pager-current">{currentPage}</span>
                  <button
                    type="button"
                    className="lms-course-forum-card__pager-btn"
                    aria-label="Página siguiente"
                    disabled={currentPage >= totalPages}
                    onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
                  >
                    <span className="material-symbols-outlined" aria-hidden="true">
                      chevron_right
                    </span>
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </section>

      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <button
            type="button"
            className="absolute inset-0 bg-on-surface/40 backdrop-blur-sm"
            aria-label="Cerrar modal"
            onClick={() => {
              setModalOpen(false);
              resetModalForm();
            }}
          />
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="new-template-title"
            className="relative z-10 w-full max-w-lg overflow-hidden rounded-2xl border border-dash-outline-variant bg-white shadow-xl"
          >
            <form method="post" className="flex flex-col" onSubmit={handleCreateSubmit}>
              <div className="flex items-center justify-between border-b border-dash-outline-variant px-6 py-4">
                <h3 id="new-template-title" className="text-xl font-semibold text-dash-on-surface">
                  Nueva plantilla
                </h3>
                <button
                  type="button"
                  className="rounded-full p-2 text-dash-on-surface-variant transition-colors hover:bg-secondary-100"
                  aria-label="Cerrar"
                  onClick={() => {
                    setModalOpen(false);
                    resetModalForm();
                  }}
                >
                  <span className="material-symbols-outlined text-[1.35rem]" aria-hidden="true">
                    close
                  </span>
                </button>
              </div>

              <div className="space-y-6 p-6">
                <div className="space-y-2">
                  <label htmlFor="new-template-name" className="ml-1 text-sm font-medium text-dash-on-surface-variant">
                    Nombre <span className="text-error-600">*</span>
                  </label>
                  <input
                    id="new-template-name"
                    type="text"
                    name="name"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    required
                    placeholder="Ej: Certificado de Excelencia 2024"
                    className={MODAL_FIELD}
                  />
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <label htmlFor="new-template-type" className="ml-1 text-sm font-medium text-dash-on-surface-variant">
                      Tipo <span className="text-error-600">*</span>
                    </label>
                    <div className="relative">
                      <select
                        id="new-template-type"
                        name="credential_type_id"
                        value={newType}
                        onChange={(e) => setNewType(e.target.value)}
                        required
                        className={`${MODAL_FIELD} appearance-none pr-10`}
                      >
                        <option value="">Seleccionar...</option>
                        {CREDENTIAL_TYPES.map((type) => (
                          <option key={type.id} value={type.id}>
                            {type.name}
                          </option>
                        ))}
                      </select>
                      <span
                        className="material-symbols-outlined pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-secondary-400"
                        aria-hidden="true"
                      >
                        keyboard_arrow_down
                      </span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label
                      htmlFor="new-template-orientation"
                      className="ml-1 text-sm font-medium text-dash-on-surface-variant"
                    >
                      Orientación
                    </label>
                    <div className="relative">
                      <select
                        id="new-template-orientation"
                        name="orientation"
                        value={newOrientation}
                        onChange={(e) => setNewOrientation(e.target.value)}
                        className={`${MODAL_FIELD} appearance-none pr-10`}
                      >
                        {ORIENTATIONS.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                      <span
                        className="material-symbols-outlined pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-secondary-400"
                        aria-hidden="true"
                      >
                        keyboard_arrow_down
                      </span>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <label
                    htmlFor="new-template-description"
                    className="ml-1 text-sm font-medium text-dash-on-surface-variant"
                  >
                    Descripción
                  </label>
                  <textarea
                    id="new-template-description"
                    name="description"
                    rows={3}
                    value={newDescription}
                    onChange={(e) => setNewDescription(e.target.value)}
                    placeholder="Breve descripción de la plantilla..."
                    className={`${MODAL_FIELD} resize-none`}
                  />
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 border-t border-dash-outline-variant bg-secondary-50 px-6 py-4">
                <button
                  type="button"
                  className="rounded-xl px-6 py-2.5 text-sm font-medium text-dash-on-surface-variant transition-colors hover:bg-secondary-100"
                  onClick={() => {
                    setModalOpen(false);
                    resetModalForm();
                  }}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending}
                  className="rounded-xl bg-dash-primary px-6 py-2.5 text-sm font-medium text-white shadow-sm transition-all hover:opacity-90 active:scale-[0.98] disabled:opacity-60"
                >
                  {createMutation.isPending ? 'Creando…' : 'Crear y abrir editor'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmModal
        open={Boolean(deleteTarget)}
        title="Eliminar plantilla"
        description={
          deleteTarget
            ? `¿Eliminar la plantilla «${deleteTarget.name}»? Se borrarán también sus versiones. Esta acción no se puede deshacer.`
            : undefined
        }
        confirmLabel="Eliminar"
        loading={deleteMutation.isPending}
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
        onClose={() => setDeleteTarget(null)}
      />
    </div>
  );
}
